import React from "react";
import PropTypes from "prop-types";
import { Accordion, Icon, Button, Pagination } from "semantic-ui-react";
import FeedbackCard from "./FeedbackCard";

const MobileAccordion = ({
  categorizedFeedbacks,
  currentPages,
  pageSize,
  onPageChange,
  onEdit,
  onDelete,
  onMarkAsRead,
  onMarkAllAsRead,
  onOpenModal,
  getUserInfo,
  markingAsRead,
  markingAllAsRead,
  categoryConfig,
  isLoading,
  activeAccordion,
  onAccordionClick,
}) => {
  const { isAllowedToEdit } = getUserInfo();
  const categories = Object.keys(categoryConfig);

  const canDeleteFeedback = (feedback) => {
    const { isAllowedToEdit, userName } = getUserInfo();
    if (isAllowedToEdit) return true;
    if (feedback.created_by && feedback.created_by === userName) return true;
    return false;
  };

  const canEditFeedback = (feedback) => {
    const { userName } = getUserInfo();
    return feedback.created_by && feedback.created_by === userName;
  };

  const getPaginatedFeedbacks = (category) => {
    const categoryFeedbacks = categorizedFeedbacks[category] || [];
    const currentPage = currentPages[category] || 1;
    const startIndex = (currentPage - 1) * pageSize;
    return categoryFeedbacks.slice(startIndex, startIndex + pageSize);
  };

  const getTotalPages = (category) => {
    const categoryFeedbacks = categorizedFeedbacks[category] || [];
    return Math.max(1, Math.ceil(categoryFeedbacks.length / pageSize));
  };

  return (
    <Accordion styled fluid style={{ marginTop: "5rem" }}>
      {categories.map((category, index) => {
        const paginatedFeedbacks = getPaginatedFeedbacks(category);
        const totalPages = getTotalPages(category);
        const currentPage = currentPages[category] || 1;
        const totalCount = (categorizedFeedbacks[category] || []).length;
        const unreadCount = (categorizedFeedbacks[category] || []).filter(
          (fb) => !fb.is_read
        ).length;
        const categoryColor = categoryConfig[category].color;
        const isMarkingAll = markingAllAsRead[category] || false;

        return (
          <div key={category}>
            <Accordion.Title
              active={activeAccordion === index}
              index={index}
              onClick={onAccordionClick}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "1rem",
                backgroundColor: "#fff",
                borderRadius: "8px",
                border: "1px solid #e0e0e0",
                marginBottom: "0.5rem",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <Icon
                  name={
                    activeAccordion === index ? "chevron down" : "chevron right"
                  }
                />
                <span
                  style={{
                    fontWeight: "bold",
                    fontSize: "1rem",
                    marginLeft: "0.5rem",
                  }}
                >
                  {category}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                {isAllowedToEdit && unreadCount > 0 && (
                  <Button
                    size="mini"
                    color="green"
                    loading={isMarkingAll}
                    disabled={isMarkingAll}
                    onClick={(e) => {
                      e.stopPropagation();
                      onMarkAllAsRead(category);
                    }}
                    style={{
                      fontSize: "0.7rem",
                      padding: "0.5rem 0.6rem",
                      marginRight: "1rem",
                    }}
                  >
                    {isMarkingAll
                      ? "Marking..."
                      : `Mark All as Read(${unreadCount})`}
                  </Button>
                )}
                {!isAllowedToEdit && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenModal(category);
                    }}
                    style={{
                      backgroundColor: categoryColor,
                      color: "white",
                      border: "none",
                      borderRadius: "50%",
                      width: "32px",
                      height: "32px",
                      cursor: "pointer",
                      fontSize: "1.2rem",
                      fontWeight: "bold",
                    }}
                  >
                    +
                  </button>
                )}
                {isAllowedToEdit && (
                  <span
                    style={{
                      backgroundColor: categoryColor,
                      color: "white",
                      borderRadius: "50%",
                      padding: "0.2rem 0.5rem",
                      fontSize: "0.8rem",
                      fontWeight: "bold",
                      width: "32px",
                      height: "32px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {totalCount}
                  </span>
                )}
              </div>
            </Accordion.Title>

            <Accordion.Content active={activeAccordion === index}>
              <div style={{ padding: "0.5rem 0" }}>
                {paginatedFeedbacks.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      color: "#666",
                      padding: "1.5rem",
                      fontSize: "0.9rem",
                    }}
                  >
                    No {category.toLowerCase()} yet.
                    <br />
                    {!isAllowedToEdit && "Be the first to contribute!"}
                  </div>
                ) : (
                  paginatedFeedbacks.map((fb) => (
                    <FeedbackCard
                      key={fb.id || fb._id}
                      feedback={fb}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      onMarkAsRead={onMarkAsRead}
                      canEdit={canEditFeedback(fb)}
                      canDelete={canDeleteFeedback(fb)}
                      isAllowedToEdit={isAllowedToEdit}
                      isBeingMarkedAsRead={
                        markingAsRead[fb.id || fb._id] || false
                      }
                    />
                  ))
                )}

                {totalPages > 1 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      marginTop: "1rem",
                    }}
                  >
                    <Pagination
                      totalPages={totalPages}
                      activePage={currentPage}
                      onPageChange={(e, data) =>
                        onPageChange(category, Number(data.activePage))
                      }
                      disabled={isLoading}
                      size="mini"
                    />
                  </div>
                )}
              </div>
            </Accordion.Content>
          </div>
        );
      })}
    </Accordion>
  );
};

MobileAccordion.propTypes = {
  categorizedFeedbacks: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        _id: PropTypes.string,
        message: PropTypes.string.isRequired,
        is_read: PropTypes.bool,
      })
    )
  ).isRequired,
  currentPages: PropTypes.objectOf(PropTypes.number).isRequired,
  pageSize: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onMarkAsRead: PropTypes.func.isRequired,
  onMarkAllAsRead: PropTypes.func.isRequired,
  onOpenModal: PropTypes.func.isRequired,
  getUserInfo: PropTypes.func.isRequired,
  markingAsRead: PropTypes.objectOf(PropTypes.bool).isRequired,
  markingAllAsRead: PropTypes.objectOf(PropTypes.bool).isRequired,
  categoryConfig: PropTypes.objectOf(
    PropTypes.shape({
      color: PropTypes.string.isRequired,
    })
  ).isRequired,
  isLoading: PropTypes.bool.isRequired,
  activeAccordion: PropTypes.number.isRequired,
  onAccordionClick: PropTypes.func.isRequired,
};

export default MobileAccordion;
